import { useState, useCallback, useEffect } from 'react';
import { LogtSearchCriteria } from '../models/logTSearchCriteria';
import { LogtSearchAllCriteria } from '../models/logTSearchAllCriteria';
import { useUserDataAuthConsumer } from '../context/UserDataContext';
import { useAuth } from '../context/AuthContext';
import { useAirline } from '../context/AirlineContext';
import { appInsights } from '../appInsights';
import { fetchAllowedGroups } from '../services/allowedGroupsService';
import { OverviewProps } from './Overview.types';

export const useOverviewState = ({
  logTs, loading, error, page, limit, totalPages, totalCount, searchLogTs, isAdmin: isAdminProp,
}: OverviewProps) => {
  const { airline, setAirline } = useAirline();
  const [acNumber, setAcNumber] = useState<string>('');
  const [flightNumber, setFlightNumber] = useState<string>('');
  const [payload, setPayload] = useState<string>('');
  const [messageType, setMessageType] = useState<string>('');
  const [smi, setSmi] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(page);
  const [pageInput, setPageInput] = useState<string>(String(page));
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [orig, setOrig] = useState<any>();
  const [dest, setDest] = useState<any>();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);
  const [isCSVLoading, setIsCSVLoading] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<string>('logtTS');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [sortLoading, setSortLoading] = useState<boolean>(false);
  const [searchExecuted, setSearchExecuted] = useState<boolean>(false);
  const [searchAllCriteria, setSearchAllCriteria] = useState<LogtSearchAllCriteria>({});
  const [fromTo, setFromTo] = useState<string>('');
  const [isXml, setIsXml] = useState<0 | 1>(0);
  const [canSelectAS, setCanSelectAS] = useState(false);
  const [canSelectQX, setCanSelectQX] = useState(false);
  const [allowedGroupsByAirline, setAllowedGroupsByAirline] = useState<Record<string, string[]>>({});
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [lastSearchCriteria, setLastSearchCriteria] = useState<LogtSearchCriteria | null>(null);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [showCsvModal, setShowCsvModal] = useState<boolean>(false);
  const [csvStartDate, setCsvStartDate] = useState<Date | null>(null);
  const [csvEndDate, setCsvEndDate] = useState<Date | null>(null);
  const [reportType, setReportType] = useState<"accessed" | "searched">("searched");
  const [fromToError, setFromToError] = useState<string | null>(null);
  const [DatePickereStartDateIsOpen, setDatePickereStartDateIsOpen] = useState(false);
  const [DatePickereEndDateIsOpen, setDatePickereEndDateIsOpen] = useState(false);
  const [dateRangeMode, setDateRangeMode] = useState<'relative' | 'specific'>('relative');
  const [relativeTimeValue, setRelativeTimeValue] = useState<number>(30);
  const [relativeTimeUnit, setRelativeTimeUnit] = useState<'min' | 'hours' | 'days'>('min');
  const [localLimit, setLocalLimit] = useState<number>(limit);
  const [calendarViewDate, setCalendarViewDate] = useState<Date>(new Date());

  // Calendar navigation
  const navigateToCurrentMonth = () => {
    const today = new Date();
    setCalendarViewDate(today);
    if (dateRangeMode === 'specific') {
      if (!startDate) {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(firstOfMonth);
      }
      if (!endDate) {
        setEndDate(today);
      }
    }
  };

  const advanceCalendarByMonth = () => {
    const nextMonth = new Date(calendarViewDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCalendarViewDate(nextMonth);
  };

  const navigateToPreviousMonth = () => {
    const prevMonth = new Date(calendarViewDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCalendarViewDate(prevMonth);
  };

  const navigateToSpecificMonth = (month: number, year: number) => {
    const newDate = new Date(year, month, 1);
    setCalendarViewDate(newDate);
  };

  // Effect to handle Escape key to close date pickers
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDatePickereStartDateIsOpen(false);
        setDatePickereEndDateIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => { document.removeEventListener('keydown', handleEscapeKey); };
  }, []);

  // Effect to initialize relative time range on component mount
  useEffect(() => {
    if (dateRangeMode === 'relative' && (!startDate || !endDate)) {
      const now = new Date();
      const end = new Date(now);
      let start = new Date(now);
      switch (relativeTimeUnit) {
        case 'min':
          start.setMinutes(start.getMinutes() - relativeTimeValue);
          break;
        case 'hours':
          start.setHours(start.getHours() - relativeTimeValue);
          break;
        case 'days':
          start.setDate(start.getDate() - relativeTimeValue);
          break;
        default:
          start.setMinutes(start.getMinutes() - relativeTimeValue);
      }
      setStartDate(start);
      setEndDate(end);
    }
  }, [dateRangeMode, relativeTimeValue, relativeTimeUnit, startDate, endDate]);

  // Effect to sync local limit with prop
  useEffect(() => { setLocalLimit(limit); }, [limit]);

  const userData = useUserDataAuthConsumer();
  const userGroups = useAuth();
  const userName = `${userData.given_name} ${userData.family_name}`;
  const userEmail = userData.email || "Unknown";

  // Fetch allowed groups dynamically from database
  useEffect(() => {
    const loadAllowedGroups = async () => {
      try {
        const token = userGroups?.userData?.access_token || '';
        if (token) {
          const groupsData = await fetchAllowedGroups(token);
          setAllowedGroupsByAirline(groupsData);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error('Error loading allowed groups:', error);
        setAllowedGroupsByAirline({
          'AS': [
            'AAD-ACARSHistoryASUsersManualExceptionsQA',
            'AAD-ACARSHistoryASUsersManualExceptionsTest',
            'AAD-ACARSHistoryASUsersManualExceptions',
            'AAD-ACARSHistoryASUsersManualExceptionsPROD',
            'AAD-ACARSHistoryASUsersAutoEnrollment',
            'AAD-ACARSHistoryASUsersAutoEnrollmentPROD',
            'AAD-ACARSHistoryASUsersAutoEnrollmentTest',
            'AAD-ACARSHistoryASUsersAutoEnrollmentQA',
          ],
          'QX': [
            'AAD-ACARSHistoryQXUsersManualExceptionsQA',
            'AAD-ACARSHistoryQXUsersManualExceptionsTest',
            'AAD-ACARSHistoryQXUsersManualExceptions',
            'AAD-ACARSHistoryQXUsersManualExceptionsPROD',
            'AAD-ACARSHistoryQXUsersAutoEnrollment',
            'AAD-ACARSHistoryQXUsersAutoEnrollmentPROD',
            'AAD-ACARSHistoryQXUsersAutoEnrollmentTest',
            'AAD-ACARSHistoryQXUsersAutoEnrollmentQA',
          ]
        });
      }
    };
    if (userGroups?.userData?.access_token) { loadAllowedGroups(); }
  }, [userGroups?.userData?.access_token]);

  // Check if user is admin
  useEffect(() => {
    setIsAdmin(isAdminProp ?? false);
  }, [isAdminProp]);

  // Log Page Visits
  useEffect(() => {
    const key = "overviewVisited";
    if (sessionStorage.getItem(key)) return;
    if (userData?.given_name && userData?.family_name && userData?.email) {
      appInsights.trackEvent({
        name: "OverviewPageVisit",
        properties: {
          timestamp: new Date().toISOString(),
          userName: `${userData.given_name} ${userData.family_name}`,
          userEmail: userData.email,
        },
      });
      sessionStorage.setItem(key, "true");
    }
  }, [userData]);

  const isCSVAuthorized = isAdminProp;

  const validateForm = useCallback(() => {
    const basicValid = Boolean(acNumber || flightNumber);
    let dateValid = false;
    if (dateRangeMode === 'relative') {
      dateValid = Boolean(relativeTimeValue && relativeTimeValue > 0);
    } else {
      dateValid = Boolean(startDate) && Boolean(endDate);
    }
    const fromToValid = !fromToError;
    setIsFormValid(basicValid && dateValid && fromToValid);
  }, [acNumber, flightNumber, startDate, endDate, fromToError, dateRangeMode, relativeTimeValue]);

  // Input handlers
  const handleAcNumberChange = (newValue: string) => { setAcNumber(newValue); };

  const handleFlightNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFlightNumber(e.target.value);
  };

  const handleKeywordSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPayload(e.target.value);
  };

  const handleFromToChange = (value: string) => {
    const upperValue = value.toUpperCase().trim();
    setFromTo(upperValue);
    if (upperValue === '') {
      setOrig(''); setDest(''); setFromToError(null);
      return;
    }
    const match = /^([A-Z]{3})\/([A-Z]{3})$/.exec(upperValue);
    if (match) {
      const [, from, to] = match;
      setOrig(from); setDest(to); setFromToError(null);
    } else {
      setFromToError("Invalid From/To format. Use SEA/AUS.");
    }
  };

  // Form validation effect
  useEffect(() => {
    const basicValid = Boolean(acNumber || flightNumber);
    let dateValid = false;
    if (dateRangeMode === 'relative') {
      dateValid = Boolean(relativeTimeValue && relativeTimeValue > 0);
    } else {
      dateValid = Boolean(startDate) && Boolean(endDate);
    }
    const fromToValid = !fromToError;
    setIsFormValid(basicValid && dateValid && fromToValid);
  }, [acNumber, flightNumber, dateRangeMode, relativeTimeValue, startDate, endDate, fromToError]);

  // Check user permissions based on dynamically loaded groups
  useEffect(() => {
    const groups: string[] = (userGroups.userData?.profile?.groups as string[]) || [];
    const allowedGroupsAS = (allowedGroupsByAirline && allowedGroupsByAirline['AS']) || [];
    const allowedGroupsQX = (allowedGroupsByAirline && allowedGroupsByAirline['QX']) || [];
    const userCanSelectAS = groups.some((group) => allowedGroupsAS.includes(group));
    const userCanSelectQX = groups.some((group) => allowedGroupsQX.includes(group));
    setCanSelectAS(userCanSelectAS);
    setCanSelectQX(userCanSelectQX);
    if (!userCanSelectAS && airline === 'AS' && userCanSelectQX) {
      setAirline('QX');
    } else if (userCanSelectAS && airline === '') {
      setAirline('AS');
    }
  }, [userGroups.userData?.profile?.groups, airline, allowedGroupsByAirline]);

  return {
    // Props pass-through
    logTs, loading, error, totalPages, totalCount, searchLogTs,
    // Context
    userData, userGroups, userName, userEmail,
    // State + setters
    airline, setAirline, acNumber, setAcNumber, flightNumber, setFlightNumber,
    payload, setPayload, messageType, setMessageType, smi, setSmi,
    startDate, setStartDate, endDate, setEndDate,
    selectedMessage, setSelectedMessage,
    currentPage, setCurrentPage, pageInput, setPageInput,
    showDetails, setShowDetails, orig, setOrig, dest, setDest,
    isFormValid, setIsFormValid, localLoading, setLocalLoading,
    tableLoading, setTableLoading, isSearchLoading, setIsSearchLoading,
    isPrintLoading, setIsPrintLoading, isCSVLoading, setIsCSVLoading,
    sortColumn, setSortColumn, sortDirection, setSortDirection,
    sortLoading, setSortLoading, searchExecuted, setSearchExecuted,
    searchAllCriteria, setSearchAllCriteria,
    fromTo, setFromTo, isXml, setIsXml,
    canSelectAS, setCanSelectAS, canSelectQX, setCanSelectQX,
    allowedGroupsByAirline, isAdmin,
    lastSearchCriteria, setLastSearchCriteria,
    searchHistory, setSearchHistory,
    showCsvModal, setShowCsvModal,
    csvStartDate, setCsvStartDate, csvEndDate, setCsvEndDate,
    reportType, setReportType,
    fromToError, setFromToError,
    DatePickereStartDateIsOpen, setDatePickereStartDateIsOpen,
    DatePickereEndDateIsOpen, setDatePickereEndDateIsOpen,
    dateRangeMode, setDateRangeMode,
    relativeTimeValue, setRelativeTimeValue,
    relativeTimeUnit, setRelativeTimeUnit,
    localLimit, setLocalLimit, calendarViewDate,
    isCSVAuthorized,
    // Calendar nav
    navigateToCurrentMonth, advanceCalendarByMonth,
    navigateToPreviousMonth, navigateToSpecificMonth,
    // Input handlers
    handleAcNumberChange, handleFlightNumberChange,
    handleKeywordSearchChange, handleFromToChange,
    validateForm,
  };
};
